import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  AlertCircle, ArrowLeft, CheckCircle2, Clock, ExternalLink,
  LogOut, RefreshCcw, ShieldCheck,
} from 'lucide-react'
import JoinShell from './JoinShell'
import SteamIcon from '@/components/site/SteamIcon'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import { Button } from '@/components/ui/button'
import { useLanguage } from '@/contexts/LanguageContext'

interface Eligibility {
  status: 'eligible' | 'not_in_guild' | 'already_member' | 'already_academy'
        | 'pending' | 'approved' | 'cooldown'
  invite_url?: string
  retry_at?: number
}

interface JoinMe {
  user: { id: string; name: string; username: string; avatar: string }
  eligibility: Eligibility
  // The linked Steam account: its name, and whether Steam itself vouched for
  // the link. A link an operator typed in shows up here unverified and does not
  // open the form. The SteamID64 stays server-side — staff read it on the review
  // page, the applicant only needs to see which account is attached.
  steam: { linked: boolean; name: string; verified: boolean }
  application: { channel_url: string; ingame_name: string } | null
  // The positions the form offers: the playing sign-up classes from Settings.
  roles: { name: string; emoji: string }[]
}

const INPUT_CLASS =
  'w-full rounded-sm border border-white/12 bg-white/5 px-3 py-2 text-sm text-foreground ' +
  'placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/60'

function Field({
  label, hint, required, group, children,
}: {
  label: string; hint?: string; required?: boolean; children: React.ReactNode
  // A set of buttons rather than one control. A <label> around several
  // buttons forwards every click on its text to the first of them.
  group?: boolean
}) {
  const { T } = useLanguage()
  const Wrapper = group ? 'div' : 'label'
  return (
    <Wrapper className="block">
      <span className="flex items-baseline gap-2">
        <span className="font-display text-[13px] font-bold tracking-tight">{label}</span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
          {required ? T.join.apply.required : T.join.apply.optional}
        </span>
      </span>
      {hint && <span className="mt-0.5 block text-[12px] text-muted-foreground">{hint}</span>}
      <div className="mt-2">{children}</div>
    </Wrapper>
  )
}

/** Full-width notice used for every "you cannot apply right now" outcome. */
function Gate({
  tone = 'neutral', icon: Icon, title, body, children,
}: {
  tone?: 'neutral' | 'good' | 'bad'
  icon: typeof AlertCircle
  title: string
  body: string
  children?: React.ReactNode
}) {
  const ring = tone === 'good' ? 'border-emerald-500/40 bg-emerald-500/5'
    : tone === 'bad' ? 'border-destructive/40 bg-destructive/5'
    : 'border-white/12 bg-card/80'
  const iconColor = tone === 'good' ? 'text-emerald-400'
    : tone === 'bad' ? 'text-destructive'
    : 'text-primary'
  return (
    <div className={`rounded-md border p-6 ${ring}`}>
      <Icon className={`h-6 w-6 ${iconColor}`} />
      <h2 className="mt-4 font-display text-xl font-bold tracking-tight">{title}</h2>
      <p className="mt-2 max-w-prose text-sm leading-relaxed text-muted-foreground">{body}</p>
      {children && <div className="mt-5 flex flex-wrap gap-3">{children}</div>}
    </div>
  )
}

export default function JoinApply() {
  const { T, lang } = useLanguage()

  const [data, setData] = useState<JoinMe | null>(null)
  const [loading, setLoading] = useState(true)
  const [unauthed, setUnauthed] = useState(false)
  const [loadError, setLoadError] = useState(false)

  // Form
  const [ingameName, setIngameName] = useState('')
  const [age, setAge] = useState('')
  const [hours, setHours] = useState('')
  const [roles, setRoles] = useState<string[]>([])
  const [playstyle, setPlaystyle] = useState('')
  const [previousClans, setPreviousClans] = useState('')
  const [referral, setReferral] = useState('')
  const [about, setAbout] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState<{ channel_url: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const res = await fetch('/api/join/me', { credentials: 'include' })
      if (res.status === 401) {
        setUnauthed(true)
        setData(null)
      } else if (res.ok) {
        setUnauthed(false)
        setData(await res.json())
      } else {
        setLoadError(true)
      }
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Steam bounces back to /apply?steam=linked|failed|taken. Read it once and
  // drop it from the URL, so a refresh does not replay a stale banner. A
  // successful link needs no banner — load() already runs and opens the form.
  const [params, setParams] = useSearchParams()
  const steamResult = params.get('steam')
  const [steamBanner, setSteamBanner] = useState('')
  useEffect(() => {
    if (!steamResult) return
    if (steamResult === 'failed') setSteamBanner(T.join.apply.steamFailed)
    else if (steamResult === 'taken') setSteamBanner(T.join.apply.steamTaken)
    const next = new URLSearchParams(params)
    next.delete('steam')
    setParams(next, { replace: true })
  }, [steamResult, params, setParams, T])

  const signOut = async () => {
    await fetch('/api/join/auth/logout', { method: 'POST', credentials: 'include' })
    window.location.href = '/'
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    const rolesMissing = !!data?.roles.length && roles.length === 0
    if (!ingameName.trim() || !age.trim() || !hours.trim() || rolesMissing) {
      setFormError(T.join.apply.validationError)
      return
    }

    setSubmitting(true)
    try {
      const res = await fetch('/api/join/applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ingame_name: ingameName.trim(),
          age: age.trim(),
          hours: hours.trim(),
          roles,
          playstyle: playstyle.trim(),
          previous_clans: previousClans.trim(),
          referral: referral.trim(),
          about: about.trim(),
        }),
      })
      if (res.ok) {
        setSubmitted(await res.json())
      } else if (res.status === 409) {
        // A precondition changed under us — the role was granted, the Steam link
        // was dropped, a second tab already applied. Re-read and let the gates
        // below explain it.
        await load()
      } else {
        setFormError(T.join.apply.submitError)
      }
    } catch {
      setFormError(T.join.apply.submitError)
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (ts: number) =>
    new Date(ts * 1000).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    })

  // ── Render ────────────────────────────────────────────────────────────────

  const body = (() => {
    if (loading) {
      return <p className="text-sm text-muted-foreground">{T.join.apply.loading}</p>
    }

    if (unauthed) {
      return (
        <Gate icon={ShieldCheck} title={T.join.apply.title} body={T.join.apply.signInBody}>
          <a href="/api/join/auth/login">
            <Button size="lg">{T.join.apply.signIn}</Button>
          </a>
        </Gate>
      )
    }

    if (loadError || !data) {
      return (
        <Gate tone="bad" icon={AlertCircle} title={T.join.errors.generic} body={T.join.apply.loadError}>
          <Button variant="outline" onClick={load} className="gap-2">
            <RefreshCcw className="h-4 w-4" />
            {T.join.apply.recheck}
          </Button>
        </Gate>
      )
    }

    // Just submitted
    if (submitted) {
      return (
        <Gate
          tone="good"
          icon={CheckCircle2}
          title={T.join.apply.successTitle}
          body={submitted.channel_url ? T.join.apply.successBody : T.join.apply.successNoChannel}
        >
          {submitted.channel_url && (
            <>
              <a href={submitted.channel_url} target="_blank" rel="noreferrer">
                <Button size="lg" className="gap-2">
                  {T.join.apply.openChannel}
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </a>
              <p className="w-full text-[12px] text-muted-foreground/70">{T.join.apply.successDm}</p>
            </>
          )}
        </Gate>
      )
    }

    const elig = data.eligibility
    const channelUrl = data.application?.channel_url ?? ''
    const channelButton = channelUrl && (
      <a href={channelUrl} target="_blank" rel="noreferrer">
        <Button className="gap-2">
          {T.join.apply.openChannel}
          <ExternalLink className="h-4 w-4" />
        </Button>
      </a>
    )

    switch (elig.status) {
      case 'not_in_guild':
        return (
          <Gate icon={AlertCircle} title={T.join.apply.notInGuildTitle} body={T.join.apply.notInGuildBody}>
            {elig.invite_url ? (
              <a href={elig.invite_url} target="_blank" rel="noreferrer">
                <Button className="gap-2">
                  {T.join.apply.openDiscord}
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </a>
            ) : (
              <p className="w-full text-sm text-muted-foreground">{T.join.apply.noInvite}</p>
            )}
            <Button variant="outline" onClick={load} className="gap-2">
              <RefreshCcw className="h-4 w-4" />
              {T.join.apply.recheck}
            </Button>
          </Gate>
        )

      case 'already_member':
        return <Gate tone="good" icon={CheckCircle2} title={T.join.apply.alreadyMemberTitle} body={T.join.apply.alreadyMemberBody} />

      case 'already_academy':
        return <Gate tone="good" icon={CheckCircle2} title={T.join.apply.alreadyAcademyTitle} body={T.join.apply.alreadyAcademyBody} />

      case 'pending':
        return (
          <Gate icon={Clock} title={T.join.apply.pendingTitle} body={T.join.apply.pendingBody}>
            {channelButton}
          </Gate>
        )

      case 'approved':
        return (
          <Gate tone="good" icon={CheckCircle2} title={T.join.apply.approvedTitle} body={T.join.apply.approvedBody}>
            {channelButton}
          </Gate>
        )

      case 'cooldown':
        return (
          <Gate
            tone="bad"
            icon={Clock}
            title={T.join.apply.cooldownTitle}
            body={T.join.apply.cooldownBody.replace('{date}', formatDate(elig.retry_at ?? 0))}
          />
        )
    }

    // Eligible — but the SteamID64 comes before the form. It is its own step
    // rather than a field because linking leaves the page for Steam, and a
    // half-filled form would not survive the round trip.
    if (!data.steam.verified) {
      return (
        <Gate icon={ShieldCheck} title={T.join.apply.steamGateTitle} body={T.join.apply.steamGateBody}>
          <a href="/api/join/steam/link">
            <Button size="lg" className="gap-2.5 font-semibold">
              <SteamIcon size={16} />
              {T.join.apply.steamConnect}
            </Button>
          </a>
        </Gate>
      )
    }

    // ── The form ────────────────────────────────────────────────────────────
    return (
      <form onSubmit={submit} className="rounded-md border border-white/12 bg-card/80 p-6 md:p-8">
        <h2 className="font-display text-xl font-bold tracking-tight">{T.join.apply.formTitle}</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">{T.join.apply.formLead}</p>

        {/* What was proven, first thing — a wrong account gets noticed before
            anything is typed, since re-linking reloads the page. */}
        <div className="mt-7 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-sm border border-primary/30 bg-primary/5 px-3.5 py-3">
          <SteamIcon size={16} />
          <div className="leading-tight">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
              {T.join.apply.steamAccount}
            </p>
            <p className="text-[13px] font-semibold tracking-tight">
              {data.steam.name || T.join.apply.steamNoName}
            </p>
          </div>
          <span className="flex items-center gap-1.5 text-[12px] text-primary">
            <ShieldCheck className="h-3.5 w-3.5" />
            {T.join.apply.steamVerified}
          </span>
          <a href="/api/join/steam/link"
             className="ml-auto text-[12px] text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline">
            {T.join.apply.steamRelink}
          </a>
        </div>

        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <Field label={T.join.apply.ingameName} hint={T.join.apply.ingameNameHint} required>
            <input className={INPUT_CLASS} value={ingameName} maxLength={64}
                   onChange={e => setIngameName(e.target.value)} />
          </Field>

          <Field label={T.join.apply.age} required>
            <input className={INPUT_CLASS} value={age} inputMode="numeric" maxLength={3}
                   onChange={e => setAge(e.target.value.replace(/\D/g, ''))} />
          </Field>

          <Field label={T.join.apply.hours} required>
            <input className={INPUT_CLASS} value={hours} inputMode="numeric" maxLength={6}
                   onChange={e => setHours(e.target.value.replace(/\D/g, ''))} />
          </Field>
        </div>

        <div className="mt-6 grid gap-6">
          {data.roles.length > 0 && (
            <Field label={T.join.apply.roles} hint={T.join.apply.rolesHint} required group>
              <div className="flex flex-wrap gap-2">
                {data.roles.map(role => {
                  const on = roles.includes(role.name)
                  return (
                    <button
                      key={role.name}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setRoles(on ? roles.filter(r => r !== role.name) : [...roles, role.name])}
                      className={`flex cursor-pointer items-center gap-1.5 rounded-sm border px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                        on
                          ? 'border-primary/70 bg-primary/10 text-foreground'
                          : 'border-white/12 bg-white/5 text-muted-foreground hover:border-white/25 hover:text-foreground'
                      }`}
                    >
                      <EmojiGlyph emoji={role.emoji} size={16} />
                      {role.name}
                    </button>
                  )
                })}
              </div>
            </Field>
          )}

          <Field label={T.join.apply.playstyle} hint={T.join.apply.playstyleHint}>
            <textarea className={`${INPUT_CLASS} min-h-16 resize-y`} value={playstyle} maxLength={300}
                      onChange={e => setPlaystyle(e.target.value)} />
          </Field>

          <Field label={T.join.apply.previousClans} hint={T.join.apply.previousClansHint}>
            <textarea className={`${INPUT_CLASS} min-h-16 resize-y`} value={previousClans} maxLength={500}
                      onChange={e => setPreviousClans(e.target.value)} />
          </Field>

          <Field label={T.join.apply.referral}>
            <input className={INPUT_CLASS} value={referral} maxLength={300}
                   onChange={e => setReferral(e.target.value)} />
          </Field>

          <Field label={T.join.apply.about} hint={T.join.apply.aboutHint}>
            <textarea className={`${INPUT_CLASS} min-h-24 resize-y`} value={about} maxLength={2000}
                      onChange={e => setAbout(e.target.value)} />
          </Field>
        </div>

        {formError && (
          <p className="mt-5 rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {formError}
          </p>
        )}

        <Button type="submit" size="lg" disabled={submitting} className="mt-7 w-full font-semibold sm:w-auto">
          {submitting ? T.join.apply.submitting : T.join.apply.submit}
        </Button>
      </form>
    )
  })()

  return (
    <JoinShell>
      <div className="mx-auto w-full max-w-3xl px-5 py-12 md:py-16">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" />
            {T.join.apply.backHome}
          </Link>

          {data?.user && (
            <div className="ml-auto flex items-center gap-2.5 rounded-sm border border-white/12 bg-white/5 px-2.5 py-1.5">
              <img src={data.user.avatar} alt="" className="h-6 w-6 rounded-full object-cover" />
              <div className="leading-tight">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                  {T.join.apply.signedInAs}
                </p>
                <p className="text-[13px] font-semibold">{data.user.name}</p>
              </div>
              <button
                onClick={signOut}
                title={T.join.apply.signOut}
                className="ml-1 cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        <h1 className="mb-7 font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl">
          {T.join.apply.title}
        </h1>

        {steamBanner && (
          <div className="mb-6 rounded-sm border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
            {steamBanner}
          </div>
        )}

        {body}
      </div>
    </JoinShell>
  )
}
